// Google sign-in button (web only). Loads Google Identity Services lazily,
// renders the official button into a div, and on success exchanges the ID
// token for a PoKu session (same adopt-merge flow as password sign-in).
// Hidden entirely when the server has no GOOGLE_CLIENT_ID configured.
import { useEffect, useRef, useState } from "react";
import { googleClientId, signInWithGoogle } from "../api/auth.js";

let gisPromise = null;
function loadGis() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Could not load Google sign-in."));
    document.head.appendChild(s);
  });
  return gisPromise;
}

export default function GoogleSignInButton({ onDone, onAvailability }) {
  const holder = useRef(null);
  const [available, setAvailable] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const clientId = await googleClientId();
      if (cancelled) return;
      if (!clientId) { // not configured — username/password only
        onAvailability?.(false);
        return;
      }
      try {
        await loadGis();
      } catch {
        onAvailability?.(false);
        if (!cancelled) setError("Could not load Google sign-in.");
        return;
      }
      if (cancelled || !holder.current) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          setError(null);
          const r = await signInWithGoogle(response.credential);
          if (r.error) setError(r.error);
          else onDone?.(r.user);
        },
      });
      window.google.accounts.id.renderButton(holder.current, {
        theme: "filled_black",
        size: "large",
        shape: "pill",
        text: "continue_with",
        width: 320,
      });
      setAvailable(true);
      onAvailability?.(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [onDone]);

  if (!available && !error) return null;
  return (
    <div className="google-signin">
      <div ref={holder} className="google-btn-holder" />
      {error && <p className="auth-error">{error}</p>}
    </div>
  );
}
